import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, CheckCircle2, Clock, MessageSquare } from "lucide-react";
import { format } from "date-fns";

interface Feedback {
  id: string;
  created_at: string;
  type: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  admin_notes: string | null;
}

export default function AdminFeedback() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");
  const { toast } = useToast();

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const { data, error } = await supabase
        .from("feedback")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setFeedbacks(data || []);
    } catch (error) {
      console.error("admin_feedback.fetch_failed");
      toast({
        title: "Error",
        description: "Failed to load feedback",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from("feedback")
        .update({ 
          status,
          ...(status === "resolved" && { resolved_at: new Date().toISOString() })
        })
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Status updated",
        description: "Feedback status has been updated successfully",
      });
      
      fetchFeedback();
    } catch (error) {
      console.error("admin_feedback.update_status_failed");
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const updateNotes = async (id: string, notes: string) => {
    try {
      const { error } = await supabase
        .from("feedback")
        .update({ admin_notes: notes })
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Notes saved",
        description: "Admin notes have been saved",
      });
    } catch (error) {
      console.error("admin_feedback.save_notes_failed");
      toast({
        title: "Error",
        description: "Failed to save notes",
        variant: "destructive",
      });
    }
  };

  const filteredFeedback = feedbacks.filter((f) => {
    if (filter === "all") return true;
    if (filter === "open") return f.status === "open";
    if (filter === "bugs") return f.type === "bug";
    if (filter === "feedback") return f.type === "feedback";
    if (filter === "features") return f.type === "feature_request";
    return true;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "bug":
        return <AlertCircle className="h-4 w-4" />;
      case "feature_request":
        return <Clock className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      open: "destructive",
      in_progress: "default",
      resolved: "secondary",
      closed: "outline",
    };
    return <Badge variant={variants[status] || "default"}>{status.replace("_", " ")}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      urgent: "destructive",
      high: "default",
      medium: "secondary",
      low: "outline",
    };
    return <Badge variant={variants[priority] || "default"}>{priority}</Badge>;
  };

  return (
    <AdminLayout title="Feedback & Bugs">
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="open">Open Only</SelectItem>
              <SelectItem value="bugs">Bugs</SelectItem>
              <SelectItem value="feedback">Feedback</SelectItem>
              <SelectItem value="features">Feature Requests</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {filteredFeedback.length} items
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFeedback.map((feedback) => (
              <Card key={feedback.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(feedback.type)}
                      <CardTitle className="text-lg">{feedback.title}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      {getPriorityBadge(feedback.priority)}
                      {getStatusBadge(feedback.status)}
                    </div>
                  </div>
                  <CardDescription>
                    Submitted {format(new Date(feedback.created_at), "PPp")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm">{feedback.description}</p>
                  
                  <div className="flex items-center gap-2">
                    <Select
                      value={feedback.status}
                      onValueChange={(value) => updateStatus(feedback.id, value)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Admin Notes</label>
                    <Textarea
                      placeholder="Add internal notes..."
                      defaultValue={feedback.admin_notes || ""}
                      onBlur={(e) => updateNotes(feedback.id, e.target.value)}
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
